import { useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AtSign } from "lucide-react";

import {
  Button,
  DataTable,
  type DataTableColumn,
  EmptyState,
  H3,
  Input,
  Mono,
  Text,
} from "@lagoon/ui";

import { api, ApiError, type DomainRule } from "../api.js";
import { Notice } from "./feedback.js";

/**
 * Domain rules: "anyone @example.com".
 *
 * Each visitor still proves control of their own address with a passcode; the
 * rule only widens who may ask for one. Removing the rule blocks the very next
 * request from anyone the rule alone was admitting.
 */
export function Domains(props: { siteId: string }): React.ReactElement {
  const queryClient = useQueryClient();
  const [domain, setDomain] = useState("");
  const [error, setError] = useState<string | null>(null);

  const rules = useQuery({
    queryKey: ["domains", props.siteId],
    queryFn: async () => (await api.listDomains(props.siteId)).domains,
  });

  const invalidate = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: ["domains", props.siteId] });

  const add = useMutation({
    mutationFn: () => api.allowDomain(props.siteId, domain.trim()),
    onSuccess: async () => {
      setDomain("");
      setError(null);
      await invalidate();
    },
    onError: (caught) => {
      setError(caught instanceof ApiError ? caught.message : "Could not allow that domain.");
    },
  });

  /**
   * Removal, which has to say when it did not happen (LGN-128).
   *
   * A refused remove left the rule in the table and the button enabled, which
   * is exactly what a successful one looks like. Everyone at the domain would
   * still be admitted, so the failure is stated in the same notice the add
   * path uses rather than swallowed.
   */
  const remove = useMutation({
    mutationFn: (value: string) => api.removeDomain(props.siteId, value),
    onSuccess: async () => {
      setError(null);
      await invalidate();
    },
    onError: (caught) => {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not remove that domain. Its people still have access.",
      );
    },
  });

  function submit(event: FormEvent): void {
    event.preventDefault();
    add.mutate();
  }

  const removing = remove.isPending;
  const removeRule = remove.mutate;
  const columns = useMemo(() => domainColumns(removeRule, removing), [removeRule, removing]);

  return (
    <section className="lg:flex lg:flex-col lg:gap-4">
      <div>
        <H3>Whole domains</H3>
        <Text size="sm" tone="muted" className="lg:mt-1">
          Anyone with an email address at these domains can sign in, each proving their own address
          with a passcode. Removing a domain blocks its people on their very next request.
        </Text>
      </div>

      {error === null ? null : <Notice tone="danger">{error}</Notice>}

      <form className="lg:flex lg:flex-wrap lg:gap-3" onSubmit={submit}>
        <Input
          className="lg:min-w-56 lg:flex-1"
          type="text"
          value={domain}
          onChange={(event) => {
            setDomain(event.target.value);
          }}
          placeholder="example.com"
          required
          aria-label="Domain to allow"
        />
        <Button variant="outline" type="submit" disabled={add.isPending || !domain.trim()}>
          {add.isPending ? "Allowing…" : "Allow domain"}
        </Button>
      </form>

      <DataTable
        caption="Domains allowed on this site"
        columns={columns}
        data={rules.data ?? []}
        loading={rules.isPending}
        loadingRows={2}
        empty={
          <EmptyState
            glow={false}
            icon={<AtSign />}
            title="No domains allowed"
            description="Add one to invite a whole organisation at once."
          />
        }
      />
    </section>
  );
}

/**
 * The columns, at module scope.
 *
 * A `cell` renderer returns JSX and is therefore indistinguishable from a
 * component to a linter reading the file. Taking what it closes over as
 * arguments keeps the definitions out of the render path and stops them being
 * rebuilt on every keystroke in the form above.
 */
function domainColumns(
  removeRule: (domain: string) => void,
  removing: boolean,
): DataTableColumn<DomainRule>[] {
  return [
    {
      accessorKey: "domain",
      header: "Domain",
      cell: (info) => (
        <span>
          anyone <Mono className="lg:text-strong">@{info.row.original.domain}</Mono>
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: (info) => (
        <span className="lg:flex lg:justify-end">
          <Button
            variant="ghost"
            size="sm"
            disabled={removing}
            onClick={() => {
              removeRule(info.row.original.domain);
            }}
          >
            Remove
          </Button>
        </span>
      ),
    },
  ];
}
